import { useCallback, useMemo, useState } from 'react';
import type ExcelJS from 'exceljs';

import { byField } from '../../../utils/sorting';

import readXlsxFileAsJson from '../../../shared/xlsxImport/readXlsxFileAsJson';
import excelFileTypeValidator from '../../../shared/xlsxImport/validators/excelFileType.validator';
import columnHeadersValidator from '../../../shared/xlsxImport/validators/columnHeaders.validator';
import noEmptyRowsValidator from '../../../shared/xlsxImport/validators/noEmptyRows.validator';
import emptyRowsTrimmer from '../../../shared/xlsxImport/processors/emptyRowsTrimmer.processor';
import entityFieldsProcessor from '../../../shared/xlsxImport/processors/entityFields.processor';
import calculateImportRangeFactory from '../../../shared/xlsxImport/calculateImportRangeFactory';
import alertTypes from '../../../shared/alertTypes';
import { ShowAlertFunction } from '../../../shared/types';

type CellObject = {
  v: any;
  t: string;
  w?: string;
};

type Validator<TValidationData> = (sheetAsJson: ExcelJS.Workbook, validationData: TValidationData) => void;

type UseUploadEntitiesOptions<TEntity, TValidationData> = {
  sheetName: string,
  columnHeaders: string[],
  minColumnsNumber: number,
  maxColumnsNumber: number,
  fieldMappings: { [key: string]: string },
  fieldNormalizers?: { [key: string]: (cell: CellObject) => CellObject },
  lastColumnLetter: string,
  dataTypesProcessor?: (data: TEntity[]) => TEntity[],
  validationData?: TValidationData,
  additionalValidators?: Validator<TValidationData>[],
  sortingField: string,
}

type ValidationResult = {
  [key: string]: string
};

export type TWithValidationError<TEntity> = TEntity & { validationData?: string }

export const mergeValidationErrors = <TEntity>(
  entities: TEntity[],
  validationErrors: ValidationResult
): TWithValidationError<TEntity>[] => {
  const entitiesWithErrors = entities as TWithValidationError<TEntity>[];
  Object.keys(validationErrors).forEach(errorIndex => {
    entitiesWithErrors[Number(errorIndex)].validationData = validationErrors[errorIndex];
  });
  return entitiesWithErrors;
};


const useUploadEntities = <TEntity extends { [key: string]: any }, TValidationData>(
  { validateEntities, isValidatingEntities }: {
    validateEntities: (
      uploadedEntities: TEntity[],
      options: {
        onSuccess: (validationErrors: ValidationResult) => void,
      }
    ) => Promise<ValidationResult>,
    isValidatingEntities: boolean,
  },
  {
    sheetName,
    columnHeaders,
    minColumnsNumber,
    maxColumnsNumber,
    fieldMappings,
    fieldNormalizers,
    lastColumnLetter,
    dataTypesProcessor,
    validationData,
    sortingField,
    additionalValidators = [],
  }: UseUploadEntitiesOptions<TEntity, TValidationData>,
  showAlert: ShowAlertFunction,
  initialState: TEntity[] = []
) => {
  const [uploadedEntities, setUploadedEntities] = useState<(TWithValidationError<TEntity>)[]>(initialState);
  const [isReadingXlsxFile, setIsReadingXlsxFile] = useState<boolean>(false);

  const clearUploadedEntities = useCallback(
    () => setUploadedEntities(initialState),
    [initialState, setUploadedEntities]
  );

  const uploadEntities = useCallback(async ({ file }: { file: File }) => {
    try {
      setIsReadingXlsxFile(true);
      const entities = await readXlsxFileAsJson({
        file,
        fileValidators: [excelFileTypeValidator],
        prevalidators: [columnHeadersValidator],
        postvalidators: [noEmptyRowsValidator, ...additionalValidators],
        fieldMappings,
        sheetName,
        getRange: calculateImportRangeFactory(lastColumnLetter),
        preprocessors: [
          emptyRowsTrimmer,
          entityFieldsProcessor(Object.values(fieldMappings)),
          ...dataTypesProcessor ? [dataTypesProcessor] : [],
        ],
        mapToJsonOptions: {
          header: columnHeaders,
        },
        validationData: {
          columnHeaders,
          ...validationData ? validationData : {},
          minColumnsNumber,
          maxColumnsNumber,
        },
        fieldNormalizers,
      } as any);

      setIsReadingXlsxFile(false);
      validateEntities(entities, {
        onSuccess: validationErrors => {
          const mergedEntities = validationErrors ? mergeValidationErrors(entities, validationErrors) : entities;
          setUploadedEntities(mergedEntities.sort(byField(sortingField)));
        },
      });
    } catch (error: any) {
      setIsReadingXlsxFile(false);
      showAlert(error.message, alertTypes.DANGER);
    }
  }, [
    validateEntities,
    showAlert,
    sheetName,
    columnHeaders,
    minColumnsNumber,
    maxColumnsNumber,
    fieldMappings,
    fieldNormalizers,
    lastColumnLetter,
    dataTypesProcessor,
    validationData,
    sortingField,
    additionalValidators,
  ]);

  const areImportsPending = useMemo(
    () => !(uploadedEntities.some(({ validationData }) => !!validationData) || uploadedEntities.length === 0),
    [uploadedEntities]
  );

  return {
    isUploadingEntities: isReadingXlsxFile || isValidatingEntities,
    uploadedEntities,
    uploadEntities,
    areImportsPending,
    clearUploadedEntities,
  };
};

export default useUploadEntities;
